import { useEffect, useRef, useState } from "react";
import HandleChange from "../../../../../Helpers/HandleChange";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { GetAddressFromLatLng } from "../../../../../reduxToolkit/Address/AddressApi";
import {
    GetAllFineByDates,
    GetAllLocationsByUserAndByDates
} from "../../../../../reduxToolkit/TrafficFines/TrafficFinesApi";
import {
    DEFAULT_LATITUDE,
    DEFAULT_LONGITUDE,
    errorMsg
} from "../../../../../Apis/functions";
import { DetailsIconsOptions, IconMoreVerticalDots } from "../../../../../components/buttons/IconButtons";
import DropDownMenuItem from "../../../../../components/DropDown/DropDownMenuItem";
import DropDownMenuProfile from "../../../../../components/DropDown/DropDownMenuProfile";
import TableFunction from "../../../../../components/table/TableFunction";
import {FINES_LOCATION_MODEL, TRAFFIC_FINES_MODEL} from "../Model/Model";
import { VIEW_TYPE } from "../../../../../enums/ViewType";
import { TABLES } from "../../../../../Apis/Tables";
import {CRITERIA_MODEL, SEARCH_MODEL} from "../../../../../components/table/Model/TableModel";
import {DATA_TYPE_MODEL, ORDER_MODEL, SEARCH_OPERATION_MODEL} from "../../../../Modals/Model/Model";
import {fieldModelName} from "../../../../../enums/fieldModelEnum";
import {SimpleSearchWithId} from "../../../../../reduxToolkit/Search/SearchApi";
import {ACTION_MODEL} from '../../../../../Helpers/ActionModel';


/**
 * @Email azapata@ibl.mx
 * @Author Ferro Zapata Arnulfo
 * @jobTitle Full Stack Developer
 * @createdAt 2025-10-07
 *
 * Hook that encapsulates all logic and state management for the FinesMap component.
 * Handles map marker generation, modal visibility, menu toggling, and Leaflet configuration.
 *
 * @param {Object} props - Component props
 * @param {Object} props.selectEmployee - Selected employee to filter fines
 * @param {boolean} props.reset - Flag to reset search/filter
 * @param {Function} props.setDirection - Function to update selected address/direction
 *
 * @returns {Object} Object containing handlers and state used in FinesMap
 * @property {Function} getActionsButtons - Generates action buttons for each row
 * @property {Function} setItemSelected - Setter for the selected item
 * @property {Object} item - Current item model
 * @property {Function} setItem - Setter to update the current item
 * @property {Function} setShowDetailsModal - Shows/hides the details modal
 * @property {Array} locations - List of locations for map markers
 * @property {boolean} showDetailModal - Indicates if the details modal is open
 * @property {string|number} itemSelected - ID of the selected item
 * @property {string} searchLocation - Value of location search filter
 * @property {Function} handleChange - Handler for form inputs
 * @property {Function} dispatch - Redux dispatch function
 * @property {Function} t - i18next translation function
 * @property {Array} fineList - List of all fines
 * @property {Array} fineListByUserId - List of fines filtered by user
 * @property {string} searchType - Current search type (USER or FINE)
 * @property {Object} listContainerRef - Ref for the scroll container
 * @property {boolean} loadingMore - Flag indicating if more data is being loaded
 */
const FinesMapApp = ({ selectEmployee, reset, setDirection, setBodyForExport, setSearch, setCriteria}) => {
    const dispatch = useDispatch();
    const { t } = useTranslation()
    const [item , setItem] = useState({...FINES_LOCATION_MODEL});
    const [itemSelected, setItemSelected] = useState();
    const [loadingMore, setLoadingMore] = useState(false);
    const [locations, setLocations] = useState([]);
    const [fineListByUserId, setFineListByUserId] = useState([]);
    const [fineList, setFineList] = useState([]);
    const [searchType, setSearchType] = useState(TABLES.FINE);
    const [searchLocation, setSearchLocation] = useState("");
    const [showDetailModal, setShowDetailsModal] = useState(false);
    const [optionModal, setOptionModal] = useState(null);

    const { handleChange } = HandleChange({ model: item, setModel: setItem });

    const timeoutRef = useRef(null);
    const listContainerRef = useRef(null);

    /**
     * Initialize default start and end dates for filtering fines
     */
    useEffect(() => {
        let range = TRAFFIC_FINES_MODEL.setRange();
        setItem((prevState) => ({
            ...prevState,
            latitude: DEFAULT_LATITUDE,
            longitude: DEFAULT_LONGITUDE,
            ...range
        }));
    }, []);

    /**
     * Fetch address from latitude and longitude and update direction
     * @param {number} lat - Latitude
     * @param {number} lng - Longitude
     */
    const getLocationData = (lat, lng) => {
        dispatch(GetAddressFromLatLng({ lat, lng, location: 7 })).then(({ payload }) => {
            if (payload?.success) {
                setDirection(payload?.location);
            }
        });
    };

    /** Update direction whenever item latitude or longitude changes */
    useEffect(() => {
        getLocationData(item?.latitude, item?.longitude);
    }, [item]);

    /** Reset search location on mount */
    useEffect(() => setSearchLocation(""), []);

    /** Clear any timeouts on unmount */
    useEffect(() => () => clearTimeout(timeoutRef.current), []);

    /**
     * Fetch data: locations and fines
     */
    const getData = async () => {
        setPage(0);
        setFineListByUserId([]);
        setFineList([]);
        setLocations([]);
        setSearchType(selectEmployee?.id ? TABLES.USER : TABLES.FINE);

        try {
            // The page is passed explicitly: setPage is asynchronous and the fetchers would
            // otherwise read the previous page and request a page the new range may not have
            await Promise.all([
                getLocations(),
                selectEmployee?.id ? getByDatesAndUserId(0) : getByDates(0),
            ]);
        } catch (err) {
            // Each fetcher reports its own API error, so only unexpected failures are logged here
            console.error('Traffic fines: date range fetch failed', err);
        }
    };

    /** Reload data when dates or reset change */
    useEffect(() => {
        if (item?.start && item?.end) getData();
    }, [item?.start, item?.end, reset]);

    const { page, rowsPerPage, setPage, openMenu, handleMenuToggle, handleClose } = TableFunction({
        data: searchType === TABLES.USER ? fineListByUserId : fineList,
        view: VIEW_TYPE.LIST.name,
        fixedPagination: false,
        hasContainer: true
    });

    /**
     * Fetch fines by employee
     */
    useEffect(() => {
        if (selectEmployee?.id) {
            setPage(0);
            setLocations([]);
            setFineListByUserId([]);
            setFineList([]);
            setSearchType(TABLES.USER);

            let range = TRAFFIC_FINES_MODEL.setRange(item?.start, item?.end);
            const body = SEARCH_MODEL.setObject({
                page: 0,
                size: rowsPerPage,
                model: TABLES.FINE_USER,
                userId: selectEmployee?.id,
                attendanceStartDate: range.start,
                attendanceEndDate: range.end
            });

            const criteria = SEARCH_MODEL.setObject({page, size:rowsPerPage, model:TABLES.FINE, rootGroup: CRITERIA_MODEL.set([userCriteria(), fineDateCriteria()])});
            setCriteria(criteria);
            setSearch(true);

            dispatch(SimpleSearchWithId({ body }),).then(({ payload:{data:{data,success,code}} }) => {
                if (success) {
                    setFineListByUserId(data || []);
                    getLocations();
                } else errorMsg(code);
            });
        }
    }, [selectEmployee]);

    /**
     * Load more data for infinite scroll
     */
    const loadMoreData = async () => {
        if (loadingMore) return;

        const activeList = searchType === TABLES.USER ? fineListByUserId : fineList;
        const isLastPage = activeList.last;
        const hasMore = !isLastPage && activeList.totalElements > activeList.content.length;

        if (!hasMore) return;

        setLoadingMore(true);
        const nextPage = page + 1;
        setPage(nextPage);

        try {
            if (searchType === TABLES.USER && selectEmployee?.id) {
                getByDatesAndUserId(nextPage);
            } else {
                getByDates(nextPage);
            }
        } catch (err) {
            errorMsg(err);
        } finally {
            setLoadingMore(false);
        }
    };

    /**
     * Fetch all locations for the selected user and date range
     * @returns {Promise<void>}
     */
    const getLocations = async () => {
        let range = TRAFFIC_FINES_MODEL.setRange(item?.start, item?.end);
        const body = {
            userId: selectEmployee?.id,
            startDate: range.start,
            endDate: range?.end
        };

        await dispatch(GetAllLocationsByUserAndByDates({ body })).then(
            ({ payload: { data: { data, success, code } } }) => {
                if (success) setLocations(data);
                else errorMsg(code);
            }
        );
    };

    /**
     * Fetch fines filtered by user and dates
     * @returns {Promise<void>}
     */
    const getByDatesAndUserId = async (pageToFetch = page) => {
        let range = TRAFFIC_FINES_MODEL.setRange(item?.start, item?.end);
        const body = SEARCH_MODEL.setObject({
            page: pageToFetch,
            size: rowsPerPage,
            model: TABLES.FINE_USER,
            userId: selectEmployee?.id,
            attendanceStartDate:range?.start,
            attendanceEndDate:range?.end
        });

        const criteria = SEARCH_MODEL.setObject({page: pageToFetch, size:rowsPerPage, model:TABLES.FINE, rootGroup: CRITERIA_MODEL.set([userCriteria(), fineDateCriteria()])});
        setCriteria(criteria);
        setSearch(true);

        dispatch(SimpleSearchWithId({ body }),).then(({ payload:{data:{data,success,code}} }) => {
            if (success) {
                setFineListByUserId((prev) => ({
                    ...data,
                    content: prev.content ? [...prev.content, ...data.content] : [...data.content],
                }));
            } else errorMsg(code);
        });
    };

    /**
     * Fetch fines filtered by date
     * @returns {Promise<void>}
     */
    const getByDates = async (pageToFetch = page) => {
        const bodyList = {
            page: pageToFetch,
            size: rowsPerPage,
        };
        let range = TRAFFIC_FINES_MODEL.setRange(item?.start, item?.end);
        const params = {
            from: range?.start,
            to: range?.end,
        };

        const criteria = SEARCH_MODEL.setObject({page: pageToFetch, size:rowsPerPage, model:TABLES.FINE, rootGroup: CRITERIA_MODEL.set([fineDateCriteria()])});
        setCriteria(criteria);
        setSearch(true);

        const response = await dispatch(GetAllFineByDates({ params, body: bodyList })).unwrap();

        // A request that never reached the service comes back without data, so the code is read
        // defensively to report the API error instead of throwing on the destructuring
        if (response?.data?.success) {
            setFineList((prev) => ({
                ...response.data.data,
                content: prev.content ? [...prev.content, ...response.data.data.content] : [...response.data.data.content],
            }));
        } else errorMsg(response?.data?.code);
    };

    /**
     * Infinite scroll listener for list container
     */
    useEffect(() => {
        const container = listContainerRef.current;
        if (!container) return;
        const handleScroll = () => {
            const { scrollTop, scrollHeight, clientHeight } = container;
            const isNearBottom = scrollHeight - scrollTop <= clientHeight + 80;

            const activeList = searchType === TABLES.USER ? fineListByUserId : fineList;
            const isLastPage = (activeList?.content?.length ?? 0) >= (activeList?.totalElements ?? 0);
            const hasMore = activeList && !isLastPage && activeList?.totalElements > activeList?.content?.length;

            if (isNearBottom && hasMore && !loadingMore) {
                loadMoreData();
            }
        };

        container.addEventListener("scroll", handleScroll);
        return () => {
            container.removeEventListener("scroll", handleScroll);
        };
    }, [fineListByUserId, fineList, loadingMore, page, rowsPerPage, searchType]);

    /**
     * Generate action buttons for each row
     * @param {Object} item - Row item
     * @param {number} index - Row index
     * @returns {JSX.Element} Rendered action buttons
     */
    const getActionsButtons = (item, index) => {
        return (
            <DropDownMenuProfile
                openMenu={openMenu}
                handleMenuToggle={handleMenuToggle}
                menuId={`menu-${item.id}`}
                icon={<IconMoreVerticalDots />}
                classNameIconContainer={"p-0 m-0"}
            >
                <DropDownMenuItem
                    index={index}
                    classNameContainerIcon={"m-0 p-0"}
                    handleClose={handleClose}
                    name={t('details')}
                    icon={<DetailsIconsOptions />}
                    click={() => {
                        setItemSelected(item.id);
                        setShowDetailsModal(true);
                        setOptionModal(ACTION_MODEL.DETAILS)
                    }}
                />
            </DropDownMenuProfile>
        );
    };

    /**
     * Builds a filter criteria for searching by the current user.
     * @returns {Object} Criteria object for filtering by user ID.
     */
    const userCriteria = () => {
        // CRITERIA_MODEL.set folds a list of rules, so a single rule still travels in an array
        return {
            "dataOption": SEARCH_OPERATION_MODEL.AND,
            "fieldType": DATA_TYPE_MODEL.UUID,
            "filterKey": fieldModelName.user,
            "operation": SEARCH_OPERATION_MODEL.EQ,
            "sort": ORDER_MODEL.ASC,
            "table": TABLES.FINE,
            "values": [selectEmployee?.id]
        };
    }

    /**
     * Builds a filter criteria for searching between range dates(fines).
     * @returns {Object} Criteria object for filtering by between days.
     */
    const fineDateCriteria = () => {
        // The same range the list requests, so the export cannot disagree with what is shown.
        // A between rule carries from/to only: an empty values list makes the service match nothing
        const range = TRAFFIC_FINES_MODEL.setRange(item?.start, item?.end);

        return {
            "dataOption": SEARCH_OPERATION_MODEL.AND,
            "fieldType": DATA_TYPE_MODEL.LONG,
            "filterKey": fieldModelName.fineDate,
            "operation": SEARCH_OPERATION_MODEL.BT,
            "sort": ORDER_MODEL.ASC,
            "table": TABLES.FINE,
            "from": range?.start,
            "to": range?.end
        };
    }


    return {
        getActionsButtons,
        setItemSelected,
        item,
        setItem,
        setShowDetailsModal,
        locations,
        showDetailModal,
        itemSelected,
        searchLocation,
        handleChange,
        dispatch,
        t,
        fineList,
        fineListByUserId,
        searchType,
        listContainerRef,
        loadingMore,
        setOptionModal,
        optionModal
    };
};

export default FinesMapApp;
